import { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { toast } from 'react-toastify';
import { Plus, CreditCard as Edit2, Trash2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { AdminMessage } from '../../models/types';
import { ImageUpload } from '../../components/UI/ImageUpload';

const AdminCommunication = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newImage, setNewImage] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [editingMessage, setEditingMessage] = useState<AdminMessage | null>(null);
  const [editImage, setEditImage] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const messagesRef = collection(db, 'adminMessages');
      const q = query(messagesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const messagesList: AdminMessage[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messagesList.push({
          id: doc.id,
          title: data.title,
          message: data.message,
          imageUrl: data.imageUrl,
          active: data.active,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          createdBy: data.createdBy
        });
      });

      setMessages(messagesList);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error(t('admin.errorLoadingMessages'));
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const timestamp = Date.now();
    const fileName = `admin-messages/${timestamp}-${file.name}`;
    const storageRef = ref(storage, fileName);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  };

  const deleteImage = async (imageUrl: string) => {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTitle.trim() || !newMessage.trim()) {
      toast.error('Title and message are required');
      return;
    }

    try {
      let imageUrl: string | undefined = undefined;

      if (newImage) {
        imageUrl = await uploadImage(newImage);
      }

      const messageData = {
        title: newTitle.trim(),
        message: newMessage.trim(),
        imageUrl,
        active: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || 'unknown'
      };

      await addDoc(collection(db, 'adminMessages'), messageData);
      setNewTitle('');
      setNewMessage('');
      setNewImage(null);
      setNewImagePreview(null);
      toast.success(t('admin.messageCreated'));
      fetchMessages();
    } catch (error) {
      console.error('Error creating message:', error);
      toast.error(t('admin.errorCreatingMessage'));
    }
  };

  const handleEdit = async (message: AdminMessage) => {
    if (!editingMessage) return;

    try {
      let imageUrl = editingMessage.imageUrl;

      if (editImage) {
        if (editingMessage.imageUrl) {
          await deleteImage(editingMessage.imageUrl);
        }
        imageUrl = await uploadImage(editImage);
      }

      await updateDoc(doc(db, 'adminMessages', message.id), {
        title: editingMessage.title,
        message: editingMessage.message,
        imageUrl,
        active: editingMessage.active,
        updatedAt: new Date()
      });

      toast.success(t('admin.messageUpdated'));
      setEditingMessage(null);
      setEditImage(null);
      setEditImagePreview(null);
      fetchMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error(t('admin.errorUpdatingMessage'));
    }
  };

  const handleDelete = async (message: AdminMessage) => {
    if (confirm(t('admin.deleteMessage'))) {
      try {
        if (message.imageUrl) {
          await deleteImage(message.imageUrl);
        }

        await deleteDoc(doc(db, 'adminMessages', message.id));
        toast.success(t('admin.messageDeleted'));
        fetchMessages();
      } catch (error) {
        console.error('Error deleting message:', error);
        toast.error(t('admin.errorDeletingMessage'));
      }
    }
  };

  const toggleActive = async (message: AdminMessage) => {
    try {
      await updateDoc(doc(db, 'adminMessages', message.id), {
        active: !message.active,
        updatedAt: new Date()
      });

      toast.success(`Message ${message.active ? 'deactivated' : 'activated'}`);
      fetchMessages();
    } catch (error) {
      console.error('Error toggling message status:', error);
      toast.error(t('admin.errorUpdatingStatus'));
    }
  };

  const startEditing = (message: AdminMessage) => {
    setEditingMessage(message);
    setEditImagePreview(message.imageUrl || null);
  };

  const cancelEditing = () => {
    setEditingMessage(null);
    setEditImage(null);
    setEditImagePreview(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('admin.systemMessages')}</h1>
        <p className="text-gray-600">Create and manage messages that appear on the homepage</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Plus className="h-6 w-6 mr-2 text-primary-600" />
          Create New Message
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message Title *
            </label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter an attention-grabbing title"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message Content *
            </label>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Write your message content here..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition-all resize-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Featured Image (Optional)
            </label>
            <p className="text-sm text-gray-500 mb-3">Add an eye-catching image to make your message stand out</p>
            <ImageUpload
              currentImageUrl={newImagePreview || undefined}
              onImageChange={(file, preview) => {
                setNewImage(file);
                setNewImagePreview(preview);
              }}
            />
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-all flex items-center font-medium shadow-md hover:shadow-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              Publish Message
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Active Messages</h2>

        <div className="space-y-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`border-2 rounded-xl p-6 transition-all ${
                message.active
                  ? 'border-primary-600 bg-gradient-to-br from-primary-50 to-white shadow-md'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              {editingMessage?.id === message.id ? (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message Title
                    </label>
                    <input
                      type="text"
                      value={editingMessage.title}
                      onChange={(e) =>
                        setEditingMessage({ ...editingMessage, title: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message Content
                    </label>
                    <textarea
                      value={editingMessage.message}
                      onChange={(e) =>
                        setEditingMessage({ ...editingMessage, message: e.target.value })
                      }
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Featured Image
                    </label>
                    <ImageUpload
                      currentImageUrl={editImagePreview || undefined}
                      onImageChange={(file, preview) => {
                        setEditImage(file);
                        setEditImagePreview(preview);
                      }}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => handleEdit(message)}
                      className="bg-primary-600 text-white px-6 py-2.5 rounded-lg hover:bg-primary-700 transition-all font-medium shadow-md hover:shadow-lg"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="bg-gray-500 text-white px-6 py-2.5 rounded-lg hover:bg-gray-600 transition-all font-medium shadow-md hover:shadow-lg"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{message.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          message.active
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : 'bg-gray-100 text-gray-600 border border-gray-300'
                        }`}>
                          {message.active ? t('admin.active') : t('admin.inactive')}
                        </span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{message.message}</p>
                    </div>
                  </div>

                  {message.imageUrl && (
                    <div className="mt-4">
                      <img
                        src={message.imageUrl}
                        alt={message.title}
                        className="w-full max-w-2xl h-64 object-cover rounded-lg shadow-md border-2 border-gray-200"
                      />
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                    <span className="text-sm text-gray-500">
                      Created: {message.createdAt.toLocaleDateString()} at {message.createdAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleActive(message)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          message.active
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300'
                            : 'bg-green-100 text-green-800 hover:bg-green-200 border border-green-300'
                        }`}
                      >
                        {message.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => startEditing(message)}
                        className="text-blue-600 hover:text-blue-800 p-2.5 hover:bg-blue-50 rounded-lg transition-all"
                        title="Edit message"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(message)}
                        className="text-red-600 hover:text-red-800 p-2.5 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete message"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {messages.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Plus className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">{t('admin.noMessages')}</p>
              <p className="text-gray-400 text-sm mt-2">Create your first message to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCommunication;
